import React from 'react';
import {
  Component,
  Position,
  getBrowser,
  LocalCache, CookieUtil, Configuration, Dispatcher
} from "puzzle";

import Dashboard from "./components/Dashboard/Dashboard";
import SubmissionsList from "./components/SubmissionsList/SubmissionsList";
// @ts-ignore
import { withRouter } from "react-router";
import SubmissionCrossover from "./components/Submission/SubmissionCrossover";
import { compose } from "@progress/kendo-data-query/dist/npm/funcs";
import Templates from "./components/Templates/Templates";
import PoliciesManager from "./components/util/Policies";
import Viewport from "./components/Viewport/Viewport";
import Preferences from "./components/Preferences/Preferences";
import * as _ from "lodash";
import ErrorHandlerManager from "./components/util/ErrorHandler/ErrorHandler";
import { ImpersonateContext, IMPERSONATE_CONTEXT } from "./components/Header/Impersonate/Context/ImpersonateContext";
import { INITIAL_USER_RESPONSE, ROUTER_PATHS } from "./Types";
import { SupplierViewport } from "./components/SupplierView/SupplierViewport/SupplierViewport";
import { TaskList } from "./components/SupplierView/TaskList/TaskList";
import { setupDayjsLocale } from "./components/util/Util";
import { ViewProvider } from "./components/Header/ViewOption/Context/ViewProvider";
import i18n from "i18next";
import GlobalInfoProvider from "./components/util/GlobalInfo";

type STATE = {
  submissionFolderTitle: 'create' | 'edit' | 'clone' | 'template_edit' | 'template_create',
  initializeUserData: any
}

class App extends Component<{}, STATE> {
  state: STATE = {
    submissionFolderTitle: 'create',
    initializeUserData: {}
  };

  taskListPath = '/view/taskList';

  constructor(props: any) {
    super(props);
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (urlSearchParams.has('force-sso-login')) {
      this.dispatch('signInRequest', Position.start);
    }
    const submissionId: string | null = urlSearchParams.get('submissionId');
    if (submissionId) {
      CookieUtil.set('submissionId', submissionId);
    }
    props.history.listen(this.beforeRouteChange);
  }

  moduleDidMount = () => {
    window.addEventListener('beforeunload', () => {
      this.dispatch('closeDetachedDrawer', Position.start, {});
    });
    this.setBrowserSpecificClass();
    ErrorHandlerManager.initialize();
  };

  /**
   * Set browser specific class name on body element
   * e.g. chrome => tp-chrome / ie => tp-ie
   */
  private setBrowserSpecificClass = () => {
    const browser = getBrowser();
    const body = document.querySelector('body');
    const className = `tp-${browser}`;
    (browser && body) && body.classList.add(className, 'pd');
  };

  /**
   * Returns submissionId and userType. If PD is reached by email link there should be submissionId as params saved in cookie.
   * Once cookie is used, should be removed from cookie
   *
   * @param initializeUserData
   */
  checkCookieParams = (initializeUserData: any): { submissionId: string, userType: string } => {
    const submissionId = CookieUtil.get('submissionId');
    CookieUtil.remove('submissionId');
    return {
      submissionId: submissionId,
      userType: _.get(initializeUserData, "userProfile.userInfo.userType")
    };
  };

  /**
   * Returns true if there is more then one active dispatchers, excluding async ones
   */
  checkForAdditionalDispatchers = (): boolean => {
    const dispArray = { ...Dispatcher.appDispatchers };
    for (const key in dispArray) {
      if (dispArray.hasOwnProperty(key) && key.startsWith('Async')) {
        delete dispArray[key];
      }
    }
    return Object.keys(dispArray).length > 1;
  };

  /**
   * Saves initializeUserData retrieved from 'initializeUser' request.
   * @param initializeUserData
   */
  setInitializeUserData(initializeUserData: any) {
    this.setState({ initializeUserData })
  }

  /**
   * Get i18n hash value from Configuration
   */
  getI18nHash() {
    return Configuration.get('I18N_HASH');
  }

  /**
   * Get initialize user data
   */
  getInitializeUserData() {
    return this.state.initializeUserData;
  }

  /**
   * Get ratelimit-reset from response header
   * @param {INITIAL_USER_RESPONSE} initialUserResponse
   */
  getRateLimitReset = (initialUserResponse: INITIAL_USER_RESPONSE) => {
    return initialUserResponse.headers['ratelimit-reset'];
  }

  /**
   * Set's html's attribute lang to show currently loaded app language, accessibility purposes.
   *
   * @param locale
   */
  setDocumentLanguage = (locale: string) => {
    const html = document.getElementsByTagName('html')[0];
    if (html) {
      const lang = locale.split('-')[0];
      html.setAttribute('lang', lang);
    }
  };

  /**
   * Sets Document title to value retrieved through initialUserData
   * @param instanceName
   */
  setInitialDocumentTitle = (instanceName: string | undefined): void => {
    if (instanceName) {
      GlobalInfoProvider.setInstanceName(instanceName);
      document.title = instanceName;
    }
  };

  /**
   * SubmissionFolderTitle change handler
   * note: Since many actions are handled by submission folder, submission folder title needs to be updated dynamically
   */
  setSubmissionFolderTitle = (submissionFolderTitle: string) => this.setState({ submissionFolderTitle });

  /**
   * Setup dayjs localization for proper dates formatting
   * @param locale
   */
  setupDayjsLocale = (locale: string) => setupDayjsLocale(locale);

  private getConfig = (): ROUTER_PATHS => {

    const config: any = {
      actions: [{
        folderDisabled: !PoliciesManager.hasSubmissionCreatePolicy,
        exact: true,
        path: ['/view/submission', '/view/submission/:id', '/view/template', '/view/template/:id'],
        component: <SubmissionCrossover
          setFolderTitle={this.setSubmissionFolderTitle}
          initializeUserData={this.state.initializeUserData}
        />,
        icon: 'add',
        text: this.translate('submissionFolderTitles.' + this.state.submissionFolderTitle)
      }, {
        exact: true,
        path: '/view/dashboard',
        component: <Dashboard initializeUserData={this.state.initializeUserData} />,
        icon: 'dashboard',
        text: this.translate('dashboard')
      }, {
        exact: true,
        path: '/view/submissionList',
        component: <SubmissionsList initializeUserData={this.state.initializeUserData} />,
        icon: 'pd-submission-list',
        text: this.translate('submissionList')
      }],
      externalApps: [],
      userActions: [{
        exact: true,
        path: '/view/preferences',
        component: <Preferences initializeUserData={this.getInitializeUserData()} />,
        icon: 'settings',
        text: this.translate('preferences')
      }]
    };

    // Got to check if PoliciesManager has policies updated and if it doesn't then do:
    // Calling setup function on PoliciesManager to update systemPolicies
    // Doing this because PoliciesManager is not initialized yet when this happens for the first time
    if (!PoliciesManager.systemPoliciesLoaded) {
      PoliciesManager.setup(this.state.initializeUserData.systemPolicies);
    }


    if (PoliciesManager.hasEnableClientSupplerView) {
      config.actions.push({
        exact: true,
        path: this.taskListPath,
        component: <TaskList
          initializeUserData={this.state.initializeUserData}
        />,
        icon: 'pd-task-list',
        text: this.translate('task')
      })
    }

    if (PoliciesManager.hasTemplatesFolderPolicy) {
      config.actions.push({
        exact: true,
        path: '/view/templates',
        component: <Templates />,
        icon: 'pd-templates',
        text: this.translate('templates')
      })
    }
    if (this.state.initializeUserData.tableauAddress && PoliciesManager.hasTableauButton) {
      config.externalApps.push({
        onClick: () => this.handleTableau(),
        icon: 'tableau',
        text: this.translate('reporting')
      })
    }

    // remove '/submission' path from submission folder paths, so user can't manually change url and be redirected to
    if (!PoliciesManager.hasSubmissionCreatePolicy) {
      const submissionPaths = config.actions[0].path;
      const createSubPathIndex = submissionPaths.findIndex((path: string) => path === '/view/submission');
      submissionPaths.splice(createSubPathIndex, 1);
    }
    // remove '/template' path from submission folder paths, so user can't manually change url and be redirected to
    if (!PoliciesManager.hasTemplateCreatePolicy) {
      const submissionPaths = config.actions[0].path;
      const createTemplatePathIndex = submissionPaths.findIndex((path: string) => path === '/view/template');
      submissionPaths.splice(createTemplatePathIndex, 1);
    }


    return config;
  };

  /**
   * Handle Tableau onClick
   */
  handleTableau = () => {
    let action = 'checkForChangesBeforeRedirect';
    if (this.props.location.pathname.includes('/view/submissionList')) {
      action = 'checkForDrawerChangesBeforeRedirect';
    }
    this.dispatch(action, '', { leaveAction: 'tableau' });
  }

  /**
   * Open tableauAddress from corresponding organization
   */
  openTableauURL = () => {
    window.open(this.state.initializeUserData.tableauAddress);
  };

  /**
   * Handles opening saf apps
   * @param leaveAction
   */
  openSafApp = (leaveAction: string) => {
    const { initializeUserData } = this.state;
    switch (leaveAction) {
      case 'safConnect':
        window.location.href = initializeUserData.connectAddress;
        break;
      case  'safTerminology':
        window.open(initializeUserData.terminologyAddress);
        break;
      case 'safTM':
        window.open(initializeUserData.tmAddress);
        break;
      default:
        console.error('Invalid appName name');
        break;
    }
  };
  /**
   * Router config for SupplierView
   */
  private getSupplierConfig = (): ROUTER_PATHS => {
    const config: any = {
      actions: [{
        exact: true,
        path: this.taskListPath,
        component: <TaskList
          initializeUserData={this.state.initializeUserData}
        />,
        icon: 'pd-task-list',
        text: this.translate('task')
      }],
      externalApps: [],
      userActions: [{
        exact: true,
        path: '/view/preferences',
        component: <Preferences initializeUserData={this.getInitializeUserData()} />,
        icon: 'settings',
        text: this.translate('preferences')
      }]
    };

    return config;
  };

  /**
   * Check if browser back button is clicked and reload route data
   * @param location
   * @param action
   */
    // @ts-ignore
  beforeRouteChange = (location: any, action: string) => {
    if (action === 'POP') {
      this.dispatch('reloadRouteData', Position.end);
    }
  };

  /**
   * Loading saved language
   *
   * @param locale
   */
  changeSavedLanguage = (locale: string) => {
    if (locale) {
      this.setDocumentLanguage(locale);
    }
  };

  getPreviousRoute = () => LocalCache.getItem('route');

  /**
   * Get viewport component
   * @param userType
   * @param enabledFeature
   * @param appVersion
   */
  getViewport = (userType: string, enabledFeature: string, appVersion: string) => {
    const showSupplierViewport = userType === 'VENDOR';
    return (
      <ViewProvider>
        {showSupplierViewport ?
          <SupplierViewport
            config={this.getSupplierConfig()}
            defaultRoute={this.taskListPath}
            appVersion={appVersion}
          /> :
          <Viewport
            config={this.getConfig()}
            defaultRoute={'/view/dashboard'}
            appVersion={appVersion}
          />}
      </ViewProvider>
    )
  }

  /**
   * Set additional child json localization
   * @param {string} locale
   * @param {any} response
   */
  setChildJsonLocalization = (locale: string, response: any) => {
    const { status, data } = response;

    if (status === 200) {
      if (data && _.isObject(data)) {
        const defaultChildContent = {
          "overwrite": "me"
        }
        if (!_.isEqual(data, defaultChildContent)) {
          i18n.addResourceBundle(locale, 'translation', data, true, true);
          i18n.addResourceBundle(locale, 'common', data, true, true);
        }
      } else {
        //not valid json file
        //this.dispatch
      }
    }
  }

  setPreviousRoute = (previousPath: string) => LocalCache.setItem('route', previousPath);

  renderBody() {
    const {
      enabledFeature,
      version,
      version_timestamp = '',
      canSwitchUser = false,
      isSwitch = false,
      userProfile
    } = this.state.initializeUserData;

    const userName = userProfile?.userInfo.userName;
    const firstName = userProfile?.userInfo.firstName;
    const userType = userProfile?.userInfo.userType;
    let appVersion = version;
    if (appVersion && (appVersion.search('SNAPSHOT') !== -1)) {
      appVersion += this.translate('timeStampVersion', { timeStampVersion: version_timestamp });
    }
    const impersonateData: IMPERSONATE_CONTEXT = {
      enabledFeature,
      canSwitchUser,
      isSwitch,
      userName,
      firstName
    };

    let view;
    if (_.isEmpty(this.state.initializeUserData)) {
      view = null;
    } else {
      view = (
        <ImpersonateContext.Provider value={impersonateData}>
          {this.getViewport(userType, enabledFeature, appVersion)}
        </ImpersonateContext.Provider>
      );
    }

    return view;
  }
}

const enhance = compose(
  withRouter
);

export default enhance(App);
